import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import { useUnit } from 'effector-react';
import { useNavigate } from 'react-router-dom';
import useMountedEvent from '../../../hooks/useMountedEvent';
import { $$app } from '../../../components/App/model';
import { hasGhsAdminUtilsRefreshKbPermissions, hasGhsAdminUtilsResetPasswordPermissions } from '../../../services/PermissionService/PermissionService';
import { $$utils } from './model';
import UtilityCard from './UtilityCard';

export default function GhsAdminUtilsPage() {
  useMountedEvent($$utils.pageMounted);

  const navigate = useNavigate();
  const userDetails = useUnit($$app.$userDetails);
  const canRefreshKnowledgeBase = hasGhsAdminUtilsRefreshKbPermissions(userDetails?.permissions);
  const canResetPassword = hasGhsAdminUtilsResetPasswordPermissions(userDetails?.permissions);

  return (
    <Container id="internal-utilities-container">
      <Typography variant="h4" color="text.primary" sx={{ mt: 1, mb: 1 }}>
        Internal Utilities
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Administrative tools for the GrayHair team. Select a utility to continue.
      </Typography>

      <Stack spacing={5} sx={{ pb: 4 }}>
        <Box>
          <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'baseline' }}>
            <Typography variant="subtitle2" color="text.primary" sx={{ textTransform: 'uppercase' }}>
              Security & Access
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Credentials, sign-on and API access controls.
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <UtilityCard
                title="Refresh KB docs"
                description="Rebuild the knowledge base document index."
                icon={<AutorenewOutlinedIcon />}
                onClick={canRefreshKnowledgeBase ? () => navigate('/ghsAdmin/utils/refresh-kb') : undefined}
                disabled={!canRefreshKnowledgeBase}
                showDisabledMessage={false}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UtilityCard
                title="Reset Password"
                description="Reset a test user's password by email address."
                icon={<LockResetOutlinedIcon />}
                onClick={canResetPassword ? () => navigate('/ghsAdmin/utils/reset-password') : undefined}
                disabled={!canResetPassword}
                showDisabledMessage={false}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UtilityCard title="SSO Configuration" description="Identity providers, SAML / OIDC connections." icon={<VpnKeyOutlinedIcon />} disabled />
            </Grid>
            <Grid item xs={12} md={6}>
              <UtilityCard title="Multi-Factor Auth" description="Enforcement rules and device enrollment." icon={<ShieldOutlinedIcon />} disabled />
            </Grid>
            <Grid item xs={12} md={6}>
              <UtilityCard title="API Keys & Tokens" description="Issue, rotate and revoke access tokens." icon={<KeyOutlinedIcon />} disabled />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'baseline' }}>
            <Typography variant="subtitle2" color="text.primary" sx={{ textTransform: 'uppercase' }}>
              Users & Accounts
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Look up and administer individual accounts.
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <UtilityCard title="User Account Details" description="View and edit account records." icon={<PersonOutlineOutlinedIcon />} disabled />
            </Grid>
            <Grid item xs={12} md={6}>
              <UtilityCard title="User Lookup" description="Search users across all customers." icon={<SearchOutlinedIcon />} disabled />
            </Grid>
            <Grid item xs={12} md={6}>
              <UtilityCard title="Account Impersonation" description="Sign in as a user to reproduce issues." icon={<GroupOutlinedIcon />} disabled />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'baseline' }}>
            <Typography variant="subtitle2" color="text.primary" sx={{ textTransform: 'uppercase' }}>
              System & Config
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Environment-level settings and diagnostics.
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <UtilityCard title="Feature Flags" description="Toggle features per environment or customer." icon={<FlagOutlinedIcon />} disabled />
            </Grid>
            <Grid item xs={12} md={6}>
              <UtilityCard title="Environment Settings" description="Runtime configuration and toggles." icon={<SettingsSuggestOutlinedIcon />} disabled />
            </Grid>
            <Grid item xs={12} md={6}>
              <UtilityCard title="Audit Logs" description="Trace administrative actions and changes." icon={<AccessTimeOutlinedIcon />} disabled />
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </Container>
  );
}
